import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output} from "@angular/core";
import {CalcInput, DateLiteral, dateLiteralFormat, LiteralType, NumberLiteral, TextLiteral, TaxReturnSourceOptions, SubtypeMode} from "../model/calc-input";
import {Instruction} from "../model/instruction";
import {UntypedFormControl} from "@angular/forms";
import {Subscription} from "rxjs";
import {calculationSource, literalSource} from "../model/transient";
import {TaxReturnSubType, SourceQuestion} from "../../../../../swaggerServices";

@Component({
    selector: 'app-calc-input',
    templateUrl: './calc-input.component.html',
    styleUrls: ['./calc-input.component.scss'],
    standalone: false
})
export class CalcInputComponent implements OnInit, OnDestroy, OnChanges {
  subscriptions: Subscription[] = [];
  @Input() calcInput: CalcInput;
  @Output() delete: EventEmitter<void> = new EventEmitter<void>();
  @Output() instructionHovered: EventEmitter<Instruction|null> = new EventEmitter<Instruction|null>();

  literalTypes?: Array<string>;
  selectedLiteralType?: LiteralType;
  literalFormControl?: UntypedFormControl;
  literalTypeFormControl?: UntypedFormControl;

  constructor() { }

  ngOnInit() {
  }

  ngOnChanges() {
    // NOTE(mike): This component is used in arrays, and re-ordering inputs will actually cause the components
    // to be re-used by index and so the calcInput actually moves between components. To handle this, we need
    // to re-initialize every time an input arg changes.
    this.init();
  }

  init() {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions.length = 0;
    this.literalTypes = null;
    this.selectedLiteralType = null;
    this.literalFormControl = null;
    this.literalTypeFormControl = null;
    if (this.calcInput.source === literalSource) {
      this.literalTypes = Object.values(LiteralType);
      const field: TextLiteral | NumberLiteral | DateLiteral | any = this.calcInput.field;
      this.selectedLiteralType = field.type === LiteralType.Text && field.displayType === LiteralType.Paragraph ?
        LiteralType.Paragraph : field.type;
      this.literalFormControl = new UntypedFormControl(field.value);
      this.literalTypeFormControl = new UntypedFormControl(this.selectedLiteralType);
      this.subscriptions.push(this.literalFormControl.valueChanges.subscribe(value => {
        const type: LiteralType = this.selectedLiteralType;
        const field: any = {
          type: type === LiteralType.Paragraph ? LiteralType.Text : type,
          value,
        };
        if (type === LiteralType.Paragraph) {
          field.displayType = LiteralType.Paragraph;
        } else if (type === LiteralType.Date) {
          field.format = dateLiteralFormat;
        }
        this.calcInput.field = field;
      }));
      this.subscriptions.push(this.literalTypeFormControl.valueChanges.subscribe(value => {
        this.selectedLiteralType = value;
        if (value === LiteralType.Text || value === LiteralType.Paragraph) {
          this.literalFormControl.setValue(this.literalFormControl.value != null ?
            this.literalFormControl.value.toString() : '');
        } else if (value === LiteralType.Number) {
          const num = this.literalFormControl.value ? Number(this.literalFormControl.value) : null;
          this.literalFormControl.setValue(isNaN(num) ? null : num);
        } else if (value === LiteralType.Date) {
          this.literalFormControl.setValue('');
        }
      }));
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  name() {
    const sourceName = this.calcInput.source.name;
    const fieldName = this.fieldName(this.calcInput);
    return [sourceName, fieldName].filter(s => !!s).join(" - ");
  }

  hasError() {
    return this.calcInput.source === calculationSource &&
      (typeof this.calcInput.field === "string" || typeof this.calcInput.field === "number");
  }

  fieldName(calcInput) {
    const {field} = calcInput;
    if (field == null) {
      return '';
    } else if (field.question) {
      return field.question;
    } else if (typeof field === "number") {
      return field.toString();
    } else if (typeof field === "string") {
      return field;
    } else if (field instanceof Instruction) {
      return field.name;
    } else {
      return '';
    }
  }

  deleteClicked() {
    this.delete.emit();
  }

  mouseenter() {
    if (this.calcInput.field instanceof Instruction) {
      this.instructionHovered.emit(this.calcInput.field);
    }
  }

  mouseleave() {
    if (this.calcInput.field instanceof Instruction) {
      this.instructionHovered.emit(null);
    }
  }

  isLiteral() {
    return this.calcInput.source === literalSource;
  }

  isSourceQuestion() {
    return this.calcInput.field && this.calcInput.field.hasOwnProperty("question");
  }

  displayYear() {
    return this.calcInput.isGlobalCalcPlan 
      && this.calcInput.source.type !== "metadata" && this.calcInput.source.type !== "taxReturn" 
      && this.isSourceQuestion();
  }

  prefix() {
    const hasTaxReturnSourceOptions = this.calcInput.options && this.calcInput.source.type === "taxReturn";
    if (hasTaxReturnSourceOptions) {
      const sourceInfo = this.taxReturnSourcePrefix(this.calcInput.options);
      return `(${sourceInfo}) ${this.calcInput.source.name} - '`;
    } 
    if (this.displayYear()) {
      const sourceQuestion = this.calcInput.field as SourceQuestion;
      const sourceInfo = `${sourceQuestion.year}`
      return `(${sourceInfo}) ${this.calcInput.source.name} - '`;
    }
    return `${this.calcInput.source.name} - '`;
  }

  private taxReturnSourcePrefix(options: TaxReturnSourceOptions): string {
    const optionsInfo = [];

    if (options.priorYear) {
      optionsInfo.push("Prior Year");
    } else {
      optionsInfo.push("Same Year");
    }

    if (options.type) {
      optionsInfo.push(options.type.type);
    } else {
      optionsInfo.push("Same Type");
    }

    if (options.subtypeMode === SubtypeMode.Prior) {
      optionsInfo.push("Prior Subtype");
    } else if (options.subtypeMode === SubtypeMode.Same) {
      optionsInfo.push("Same Subtype");
    } else if (options.subtypeMode === SubtypeMode.Chain) {
      const chain: TaxReturnSubType[] = options.subtypeChain;
      const label = `Latest from ${chain.map(s => s.subType).join(' → ')}`;
      optionsInfo.push(label);
    } else if (options.subtypeMode === SubtypeMode.Specific) {
      const subtype: TaxReturnSubType = options.subtypeValue;
      optionsInfo.push(subtype.subType);
    }

    return optionsInfo.join(', ');
  }
}
